/**
 * Vote Service - Handles voting operations for setlist sheets
 */

import { fetchWithAuth, API_URL } from './ApiService';

class VoteService {
  /**
   * Cast or update a vote on a sheet in a setlist
   * @param {string} setlistId - The setlist ID
   * @param {string} sheetId - The sheet ID
   * @param {string} voteType - Type of vote: 'like', 'love', 'meh', 'dislike'
   * @param {string} comment - Optional comment about the vote
   * @returns {Promise<Object>} Vote response with updated summary
   */
  async castVote(setlistId, sheetId, voteType, comment = '') {
    try {
      const response = await fetchWithAuth(`${API_URL}/votes/${setlistId}/${sheetId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          vote_type: voteType,
          comment: comment
        })
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to cast vote');
      }

      return response.data;
    } catch (error) {
      console.error('Error casting vote:', error);
      throw error;
    }
  }

  /**
   * Remove a vote on a sheet in a setlist
   * @param {string} setlistId - The setlist ID
   * @param {string} sheetId - The sheet ID
   * @returns {Promise<Object>} Updated vote summary
   */
  async removeVote(setlistId, sheetId) {
    try {
      const response = await fetchWithAuth(`${API_URL}/votes/${setlistId}/${sheetId}`, {
        method: 'DELETE'
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to remove vote');
      }

      return response.data;
    } catch (error) {
      console.error('Error removing vote:', error);
      throw error;
    }
  }

  /**
   * Get all votes for a setlist
   * @param {string} setlistId - The setlist ID
   * @returns {Promise<Object>} Object with vote data for each sheet
   */
  async getSetlistVotes(setlistId) {
    try {
      const response = await fetchWithAuth(`${API_URL}/votes/${setlistId}`);

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch setlist votes');
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching setlist votes:', error);
      throw error;
    }
  }

  /**
   * Get vote summary for a specific sheet in a setlist
   * @param {string} setlistId - The setlist ID
   * @param {string} sheetId - The sheet ID
   * @returns {Promise<Object>} Vote summary for the sheet
   */
  async getSheetVoteSummary(setlistId, sheetId) {
    try {
      const response = await fetchWithAuth(`${API_URL}/votes/${setlistId}/${sheetId}`);

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch sheet vote summary');
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching sheet vote summary:', error);
      throw error;
    }
  }

  /**
   * Get current user's votes for a setlist
   * @param {string} setlistId - The setlist ID
   * @returns {Promise<Array>} Array of user's votes
   */
  async getMyVotes(setlistId) {
    try {
      const response = await fetchWithAuth(`${API_URL}/votes/${setlistId}/my-votes`);

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch user votes');
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching user votes:', error);
      
      // If it's an authentication/authorization error, don't throw - return empty array
      if (error.message && (error.message.includes('401') || error.message.includes('403') || error.message.includes('Unauthorized'))) {
        console.warn('User not authorized to access votes, returning empty array');
        return [];
      }
      
      throw error;
    }
  }

  /**
   * Calculate vote percentages for display
   * @param {Object} voteSummary - Vote summary object from API
   * @returns {Object} Vote summary with percentages
   */
  calculateVotePercentages(voteSummary) {
    if (!voteSummary || voteSummary.total === 0) {
      return {
        like: { count: 0, percentage: 0, voters: [] },
        love: { count: 0, percentage: 0, voters: [] },
        meh: { count: 0, percentage: 0, voters: [] },
        dislike: { count: 0, percentage: 0, voters: [] },
        total: 0
      };
    }

    const result = { ...voteSummary };
    
    // Calculate percentages
    ['like', 'love', 'meh', 'dislike'].forEach(voteType => {
      const count = result[voteType]?.count || 0;
      result[voteType] = {
        ...result[voteType],
        count,
        percentage: voteSummary.total > 0 ? Math.round((count / voteSummary.total) * 100) : 0
      };
    });

    return result;
  }

  /**
   * Get vote type emoji/icon
   * @param {string} voteType - The vote type
   * @returns {string} Emoji or icon character
   */
  getVoteEmoji(voteType) {
    const emojis = {
      like: '👍',
      love: '❤️',
      meh: '🤷',
      dislike: '👎'
    };
    return emojis[voteType] || '❓';
  }

  /**
   * Get vote type color for UI styling
   * @param {string} voteType - The vote type
   * @returns {string} CSS color class or hex code
   */
  getVoteColor(voteType) {
    const colors = {
      like: '#22c55e',    // green-500
      love: '#ef4444',    // red-500
      meh: '#f59e0b',     // amber-500
      dislike: '#6b7280'  // gray-500
    };
    return colors[voteType] || '#6b7280';
  }

  /**
   * Get the most popular vote type for a sheet
   * @param {Object} voteSummary - Vote summary object
   * @returns {string|null} The most popular vote type or null if no votes
   */
  getMostPopularVote(voteSummary) {
    if (!voteSummary || voteSummary.total === 0) return null;

    let maxCount = 0;
    let mostPopular = null;

    ['love', 'like', 'meh', 'dislike'].forEach(voteType => {
      const count = voteSummary[voteType]?.count || 0;
      if (count > maxCount) {
        maxCount = count;
        mostPopular = voteType;
      }
    });

    return mostPopular;
  }

  /**
   * Check if current user has voted on a sheet
   * @param {Array} userVotes - Array of user's votes from getMyVotes()
   * @param {string} sheetId - The sheet ID to check
   * @returns {Object|null} The vote object if found, null otherwise
   */
  getUserVoteForSheet(userVotes, sheetId) {
    if (!userVotes || !Array.isArray(userVotes)) return null;
    return userVotes.find(vote => vote.sheet_id === sheetId || vote.sheet_id._id === sheetId) || null;
  }
}

// Export singleton instance
const voteService = new VoteService();
export default voteService;